import React, { createContext, useContext, useEffect, useState} from 'react';
import Routes from '../../routes';
import Config from '../../config';

import Page from '@lwt-helix/page';
import Modal, { ModalProps } from '@lwt-helix/modal';

import {
    ReadOnlyAppContextInterface,
    CommonConfigInterface,
    DomainConfigInterface,
    Localization
} from '../../types';
import Authentication from '../authentication/Authentication';
import { AppStoreApi, useAppStore } from '../../stores/appStore';
import { useEffectAsync } from '../../common/hooks';

export const ReadonlyAppContext = createContext<ReadOnlyAppContextInterface>({
    config: {
        ...(Config.get('common') as CommonConfigInterface),
        ...(Config.get(window.location.host) as DomainConfigInterface)
    }
});

const App = (): React.ReactElement => {
    const [isAppReady, setIsAppReady] = useState(false);
    const {
        config: {
            lwtApp,
            ui: { faviconUrl, cssAssetsCdn }
        }
    } = useContext<ReadOnlyAppContextInterface>(ReadonlyAppContext);

    const {
        culture,
        modal: modalProps,
        showModal,
        setLocalization,
        setFormValidationErrors,
        modalErrors,
        setModalErrors
    }: AppStoreApi = useAppStore();

    // cleanup.didCancel check is not required in this effect. We are setting global state, not local.
    useEffectAsync(
        async (): Promise<void> => {
            const getResources = async (): Promise<Localization> => {
                const defaultResources = await import(
                    `../../localization/default/resources-${culture}`
                );

                const tenantResources = await import(
                    `../../localization/${lwtApp}/resources-${culture}`
                );

                return {
                    default: defaultResources.default,
                    tenant: tenantResources.default
                };
            };

            const getFormValidationErrors = async (): Promise<{
                [errorType: string]: string | { [deepProp: string]: string }[];
            }> => {
                let errors;

                errors = await import(
                    `../../localization/${lwtApp}/validation-errors-${culture}`
                ).catch(
                    async (): Promise<{
                        [errorType: string]:
                            | string
                            | { [deepProp: string]: string }[];
                    }> => {
                        return await import(
                            `../../localization/default/validation-errors-${culture}`
                        );
                    }
                );

                return errors.default;
            };

            const getModalErrors = async (): Promise<{
                [errorCode: number]: ModalProps;
            }> => {
                const errors = await import(
                    `../../localization/default/modal-errors-${culture}`
                );

                return errors.default;
            };

            const resources = await getResources();

            setLocalization(resources);

            const errors = await getFormValidationErrors();

            setFormValidationErrors(errors);

            const modalErrors = await getModalErrors();

            setModalErrors(modalErrors);

            setIsAppReady(true);
        },
        (): void => {
            showModal && showModal(modalErrors[0]);
        },
        [culture]
    );

    useEffect((): void => {
        const favicon = document.getElementById('favicon') as HTMLLinkElement;

        if (faviconUrl && favicon) {
            favicon.href = faviconUrl;
        }

        const cssAssets = document.getElementById(
            'css-assets'
        ) as HTMLLinkElement;

        if (cssAssetsCdn && cssAssets) {
            cssAssets.href = cssAssetsCdn;
        }
    }, [faviconUrl, cssAssetsCdn]);

    return (
        <Page>
            <Modal {...modalProps} />
            {isAppReady && (
                <Authentication>
                    <Routes />
                </Authentication>
            )}
        </Page>
    );
};

export default App;
